import { useEffect, useMemo, useRef } from 'react'

import { useAnalytics } from '@/shared/lib/integrations/analytics/useAnalytics'
import { useBlocker } from '@/shared/hooks/useBlocker'
import type { JourneyWithUnderscores } from '@/types/journey'

import { SCREENS } from './Levels/config'

import type { EnrichedLevel } from './Levels/context'

export function useTrackJourneyAnalytics({
  journey
}: {
  journey: JourneyWithUnderscores
}) {
  const { track, normalize, shouldSendOnce } = useAnalytics()

  const { checkBlockCondition } = useBlocker()
  const blockResult = checkBlockCondition(journey?._blockCondition)

  useEffect(() => {
    if (!journey) return

    if (shouldSendOnce('journey_viewed', journey.id)) {
      track.journey.viewed(normalize.journey(journey))
    }
  }, [journey])

  useEffect(() => {
    if (!journey) return

    if (
      !blockResult.unlocked &&
      shouldSendOnce('journey_blocked', journey.id)
    ) {
      track.journey.blocked(normalize.journey(journey))
    }
  }, [journey, blockResult])
}

export function useTrackLevelAnalytics({
  level,
  currentScreen
}: {
  level: EnrichedLevel
  currentScreen: string
}) {
  const { track, normalize, shouldSendOnce } = useAnalytics()
  const levelId = level?.id
  const normLevel = useMemo(
    () => (level ? normalize.level(level) : null),
    [levelId]
  )
  const { checkBlockCondition } = useBlocker()
  const blockResult = checkBlockCondition(level?._blockCondition)

  const trackLevelLeave = () => {
    if (!levelId || !normLevel) return

    if (shouldSendOnce('level_leave', levelId)) {
      track.level.leave(normLevel)
    }
  }

  useEffect(() => {
    if (!levelId || !normLevel || !currentScreen) return

    if (!blockResult.unlocked && shouldSendOnce('level_blocked', levelId)) {
      track.level.blocked(normLevel)
    }

    if (
      currentScreen == SCREENS.INITIAL_SCREEN &&
      shouldSendOnce('level_viewed', levelId)
    ) {
      track.level.viewed(normLevel)
    }

    if (currentScreen == SCREENS.STEPS_SCREEN) {
      if (level?._levelContinue && shouldSendOnce('level_continue', levelId)) {
        track.level.continue(normLevel)
      }

      if (!level?._levelContinue && shouldSendOnce('level_started', levelId)) {
        track.level.started(normLevel)
      }
    }

    if (
      currentScreen == SCREENS.OUT_OF_TIME_SCREEN &&
      shouldSendOnce('level_out_of_time', levelId)
    ) {
      track.level.outOfTime(normLevel)
    }

    if (
      currentScreen == SCREENS.OUT_OF_HEARTS_SCREEN &&
      shouldSendOnce('level_out_of_hearts', levelId)
    ) {
      track.level.outOfHearts(normLevel)
    }

    if (
      currentScreen == SCREENS.FINISH_SCREEN &&
      shouldSendOnce('level_completed', levelId)
    ) {
      track.level.completed(normLevel)
    }
  }, [currentScreen, levelId, normLevel, blockResult])

  return {
    trackLevelLeave
  }
}
